import type { Branding } from '~/helpers/branding/branding-context'
import { DEFAULT_BRANDING } from '~/helpers/branding/branding-context'

/**
 * Logo Studeva, posé quand l'organisation n'a rien personnalisé : celle du
 * super-admin, et toute organisation qui garde l'apparence par défaut.
 *
 * Deux fichiers, nommés d'après la couleur de leur FOND : `logo-light` (encre
 * aubergine) se pose sur un fond clair, `logo-dark` (encre lavande) sur un fond
 * sombre.
 */
export const STUDEVA_LOGO = {
  onLight: '/logo-light.webp',
  onDark: '/logo-dark.webp',
} as const

/**
 * Où le logo est posé. `page` : fond de page ou carte, clair en thème clair.
 * `bar` : la barre latérale du back-office, aubergine en thème clair
 * et lavande en thème sombre — le fond s'y inverse par rapport au thème.
 */
export type LogoSurface = 'page' | 'bar'

/**
 * Les fichiers du logo Studeva pour chaque thème, ou null si l'organisation a
 * personnalisé son identité.
 *
 * « Rien de personnalisé » = ni logo ni nom propre : une organisation en marque
 * blanche qui n'a changé que son nom garde son nom en texte, elle n'hérite pas
 * du logo Studeva.
 *
 * Rien tant que la config n'est pas chargée : le nom vaut alors encore le
 * défaut, et le logo Studeva clignoterait sur l'écran de connexion d'un client
 * en marque blanche avant que son propre logo n'arrive.
 */
export function studevaLogo(
  branding: Pick<Branding, 'logo' | 'logoDark' | 'appName' | 'loaded'>,
  surface: LogoSurface,
): { light: string, dark: string } | null {
  if (!branding.loaded) return null
  if (branding.logo || branding.logoDark) return null
  if (branding.appName !== DEFAULT_BRANDING.appName) return null
  return surface === 'page'
    ? { light: STUDEVA_LOGO.onLight, dark: STUDEVA_LOGO.onDark }
    : { light: STUDEVA_LOGO.onDark, dark: STUDEVA_LOGO.onLight }
}
